import React from 'react';
import { Heart, X, MapPin, Ban, Flag, RotateCcw, Sparkles } from 'lucide-react';
import { getMbtiCompatibility } from './PersonalityType';


export default function DiscoverView({
  profile, profiles, currentIndex, user, loading, matches,
  discoverPhotoIndex, setDiscoverPhotoIndex,
  expandedScores, setExpandedScores,
  expandedSections, setExpandedSections,
  profileForm, setEditingProfile,
  swipeDirection, touchDelta, lastSwipe,
  locationMode, setLocationMode,
  postcodeInput, setPostcodeInput,
  exploreInput, setExploreInput,
  exploreSuggestions, setExploreSuggestions,
  showSuggestions, setShowSuggestions,
  exploreLocation, setExploreLocation,
  locationLoading, locationError, setLocationError,
  densityData, densityLoading,
  exploreMapRef, exploreMapInstance,
  locationFailed, isSwipingRef,
  onSwipe, onTouchStart, onTouchMove, onTouchEnd,
  onUndoLastSwipe, onBlockUser, onOpenReportModal,
  onPostcodeLookup, onExploreInputChange, onExploreLookup,
  onSelectSuggestion, onResetExplore, onFetchDensityData,
  setCurrentView, onNeedsVerification,
}) {
  const currentProfile = profiles[currentIndex];

  return (
          <div style={{ padding: '20px', minHeight: '500px', position: 'relative' }}>
            {/* Profile completeness — slim banner replaces the previous
                large gradient banner that pushed the swipe deck off-screen.
                Refactor 3 May 2026 per JEzza Find-page UX feedback. The
                wider DiscoverView redesign is captured separately as a
                roadmap card. */}
            {profile && (() => {
              const missing = [];
              if (!profile.bio || profile.bio.trim().length === 0) missing.push({ field: 'bio', section: 'about' });
              if (!profile.interests || profile.interests.length === 0) missing.push({ field: 'interests', section: 'interests' });
              if (!profile.photos || profile.photos.length < 3) missing.push({ field: 'photos', section: 'photos', count: profile.photos ? 3 - profile.photos.length : 3 });
              if ((!profile.genderPreference || profile.genderPreference.length === 0) && (!profile.preferences?.interestedIn || profile.preferences.interestedIn.length === 0)) {
                missing.push({ field: 'preferences', section: 'about' });
              }

              if (missing.length === 0) return null;

              const summary = missing.length === 1
                ? (missing[0].field === 'photos' ? `Add ${missing[0].count} more photo${missing[0].count === 1 ? '' : 's'}` :
                   missing[0].field === 'bio' ? 'Add a bio' :
                   missing[0].field === 'interests' ? 'Select interests' :
                   'Set who you\'re interested in')
                : `${missing.length} steps to be visible`;

              return (
                <div
                  onClick={() => {
                    setCurrentView('profile');
                    setEditingProfile(true);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    setExpandedSections({...expandedSections, [missing[0].section]: true});
                  }}
                  style={{
                    padding: '10px 14px',
                    marginBottom: '16px',
                    background: 'var(--surface-alt, #fef3c7)',
                    border: '1px solid #f59e0b',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setCurrentView('profile');
                      setEditingProfile(true);
                    }
                  }}
                >
                  <span style={{ fontSize: '16px', flexShrink: 0 }}>⚠️</span>
                  <span style={{ flex: 1, color: '#78350f', fontWeight: 500 }}>
                    Profile hidden — {summary}
                  </span>
                  <span style={{ color: '#92400e', fontWeight: 600, fontSize: '12px' }}>
                    Fix →
                  </span>
                </div>
              );
            })()}

            {/* Explore mode banner */}
            {exploreLocation && (
              <div style={{
                padding: '12px 16px',
                marginBottom: '16px',
                background: 'var(--surface-alt)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                flexWrap: 'wrap'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MapPin size={18} style={{ color: 'var(--primary)' }} />
                  <span style={{ 
                    fontSize: '14px', 
                    color: 'var(--text-primary)', 
                    fontWeight: '600' 
                  }}>
                    Exploring {exploreLocation.name}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => { 
                      setExploreLocation(null); 
                      setLocationMode('explore-input'); 
                      onFetchDensityData(); 
                    }}
                    style={{
                      padding: '6px 14px',
                      background: 'var(--surface)',
                      color: 'var(--primary)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Search Somewhere Else
                  </button>
                  <button
                    onClick={onResetExplore}
                    style={{
                      padding: '6px 14px',
                      background: 'var(--primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Back to My Area
                  </button>
                </div>
              </div>
            )}

            {/* Location fallback — shown when geolocation fails and no saved location */}
            {locationFailed && !profile?.location?.coordinates && !exploreLocation && (
              <div style={{
                padding: '20px',
                marginBottom: '20px',
                background: 'var(--surface)',
                border: '2px solid var(--border)',
                borderRadius: '16px'
              }}>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <MapPin size={32} style={{ color: 'var(--primary)', marginBottom: '8px' }} />
                  <h4 style={{ 
                    margin: '0 0 6px 0', 
                    fontSize: '17px', 
                    fontWeight: '700',
                    color: 'var(--text-primary)'
                  }}>
                    Where are you?
                  </h4>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '14px', 
                    color: 'var(--text-secondary)' 
                  }}>
                    Location services aren't available. Enter your postcode so we can show you people nearby.
                  </p>
                </div>
                
                {locationError && (
                  <div style={{
                    padding: '10px 14px',
                    marginBottom: '12px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: 'var(--danger)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    textAlign: 'center'
                  }}>
                    {locationError}
                  </div>
                )}
                
                <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                  <input
                    type="text"
                    value={postcodeInput}
                    onChange={(e) => setPostcodeInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="Enter postcode (e.g. 4810)"
                    maxLength={4}
                    style={{
                      flex: 1,
                      padding: '12px 14px',
                      border: '2px solid var(--border)',
                      borderRadius: '10px',
                      fontSize: '15px',
                      background: 'var(--surface-dim)',
                      color: 'var(--text-primary)',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && onPostcodeLookup()}
                  />
                  <button
                    onClick={onPostcodeLookup}
                    disabled={locationLoading || postcodeInput.length !== 4}
                    style={{
                      padding: '12px 20px',
                      background: locationLoading || postcodeInput.length !== 4 
                        ? 'var(--text-muted)' 
                        : 'var(--primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: locationLoading || postcodeInput.length !== 4 ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s'
                    }}
                  >
                    {locationLoading ? '...' : 'Set Location'}
                  </button>
                </div>

                <div style={{
                  textAlign: 'center',
                  fontSize: '13px',
                  color: 'var(--text-muted)'
                }}>
                  or
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '12px', position: 'relative' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <input
                      type="text"
                      value={exploreInput}
                      onChange={(e) => onExploreInputChange(e.target.value)}
                      onFocus={() => { if (exploreSuggestions.length > 0) setShowSuggestions(true); }}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      placeholder="Explore a different area (e.g. Brisbane)"
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: '2px solid var(--border)',
                        borderRadius: '10px',
                        fontSize: '15px',
                        background: 'var(--surface-dim)',
                        color: 'var(--text-primary)',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && onExploreLookup()}
                    />
                    {showSuggestions && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        background: 'var(--surface)',
                        border: '2px solid var(--border)',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        zIndex: 100,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                      }}>
                        {exploreSuggestions.length > 0 ? exploreSuggestions.map((s, i) => (
                          <button
                            key={i}
                            onMouseDown={(e) => { e.preventDefault(); onSelectSuggestion(s); }}
                            style={{
                              width: '100%',
                              padding: '10px 14px',
                              background: 'none',
                              border: 'none',
                              borderBottom: i < exploreSuggestions.length - 1 ? '1px solid var(--border-light)' : 'none',
                              color: 'var(--text-primary)',
                              fontSize: '14px',
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                            onMouseEnter={(e) => e.target.style.background = 'var(--surface-alt)'}
                            onMouseLeave={(e) => e.target.style.background = 'none'}
                          >
                            <MapPin size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                            <span>{s.display}</span>
                          </button>
                        )) : (
                          <div style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: '13px' }}>
                            Not listed? Press <strong>Explore</strong> to search
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={onExploreLookup}
                    disabled={locationLoading || !exploreInput.trim()}
                    style={{
                      padding: '12px 20px',
                      background: locationLoading || !exploreInput.trim() 
                        ? 'var(--text-muted)' 
                        : 'var(--primary-dark)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: locationLoading || !exploreInput.trim() ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s'
                    }}
                  >
                    {locationLoading ? '...' : 'Explore'}
                  </button>
                </div>
              </div>
            )}

            {/* Explore option — always available as a subtle link even when location works */}
            {!locationFailed && !exploreLocation && locationMode !== 'explore-input' && profile?.location?.coordinates && (
              <div style={{ 
                marginBottom: '16px', 
                textAlign: 'center' 
              }}>
                <button
                  onClick={() => { setLocationMode('explore-input'); onFetchDensityData(); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary)',
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px'
                  }}
                >
                  <MapPin size={14} />
                  Explore a different area
                </button>
              </div>
            )}

            {/* Explore map panel — shown when user clicks "Explore a different area" */}
            {locationMode === 'explore-input' && !exploreLocation && (
              <div style={{
                marginBottom: '16px',
                background: 'var(--surface)',
                border: '2px solid var(--border)',
                borderRadius: '16px',
                overflow: 'visible',
                position: 'relative'
              }}>
                {/* Header */}
                <div style={{
                  padding: '14px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid var(--border)',
                  borderRadius: '14px 14px 0 0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MapPin size={18} style={{ color: 'var(--primary)' }} />
                    <span style={{ 
                      fontSize: '15px', 
                      fontWeight: '700', 
                      color: 'var(--text-primary)' 
                    }}>
                      Explore Australia
                    </span>
                  </div>
                  <button
                    onClick={() => { setLocationMode('auto'); setExploreInput(''); setLocationError(''); setExploreSuggestions([]); setShowSuggestions(false); }}
                    style={{
                      background: 'var(--surface-alt)',
                      color: 'var(--text-secondary)',
                      border: 'none',
                      borderRadius: '8px',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    ✕
                  </button>
                </div>

                {/* Leaflet map container */}
                <div style={{ position: 'relative', overflow: 'hidden' }}>
                  {densityLoading && (
                    <div style={{ 
                      position: 'absolute',
                      top: 0, left: 0, right: 0, bottom: 0,
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      background: 'var(--surface)',
                      zIndex: 1000,
                      color: 'var(--text-muted)',
                      fontSize: '14px'
                    }}>
                      Loading map...
                    </div>
                  )}
                  <div 
                    ref={exploreMapRef}
                    style={{ 
                      width: '100%', 
                      height: '380px',
                      background: 'var(--surface-dim)'
                    }} 
                  />
                </div>

                {/* Legend + hint */}
                <div style={{
                  padding: '10px 16px',
                  borderTop: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '11px',
                    color: 'var(--text-muted)'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'hsl(210, 70%, 55%)', display: 'inline-block' }} />
                      Fewer
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'hsl(30, 85%, 48%)', display: 'inline-block' }} />
                      More
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'hsl(0, 90%, 45%)', display: 'inline-block' }} />
                      Most
                    </span>
                  </div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    Tap a dot to explore that area
                  </span>
                </div>

                {/* Manual search fallback */}
                <div style={{
                  padding: '12px 16px',
                  borderTop: '1px solid var(--border)',
                  display: 'flex',
                  gap: '10px',
                  position: 'relative',
                  zIndex: 1001
                }}>
                  <div style={{ flex: 1, position: 'relative', zIndex: 1002 }}>
                    <input
                      type="text"
                      value={exploreInput}
                      onChange={(e) => onExploreInputChange(e.target.value)}
                      onFocus={() => { if (exploreSuggestions.length > 0) setShowSuggestions(true); }}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      placeholder="Or type a city name..."
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid var(--border)',
                        borderRadius: '10px',
                        fontSize: '14px',
                        background: 'var(--surface-dim)',
                        color: 'var(--text-primary)',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && onExploreLookup()}
                    />
                    {showSuggestions && (
                      <div style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: 0,
                        right: 0,
                        marginBottom: '4px',
                        background: 'var(--surface)',
                        border: '2px solid var(--border)',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        zIndex: 1002,
                        boxShadow: '0 -8px 24px rgba(0,0,0,0.25)'
                      }}>
                        {exploreSuggestions.length > 0 ? exploreSuggestions.map((s, i) => (
                          <button
                            key={i}
                            onMouseDown={(e) => { e.preventDefault(); onSelectSuggestion(s); }}
                            style={{
                              width: '100%',
                              padding: '10px 14px',
                              background: 'none',
                              border: 'none',
                              borderBottom: i < exploreSuggestions.length - 1 ? '1px solid var(--border-light)' : 'none',
                              color: 'var(--text-primary)',
                              fontSize: '14px',
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                            onMouseEnter={(e) => e.target.style.background = 'var(--surface-alt)'}
                            onMouseLeave={(e) => e.target.style.background = 'none'}
                          >
                            <MapPin size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                            <span>{s.display}</span>
                          </button>
                        )) : (
                          <div style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: '13px' }}>
                            Not listed? Press <strong>Go</strong> to search
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={onExploreLookup}
                    disabled={locationLoading || !exploreInput.trim()}
                    style={{
                      padding: '10px 16px',
                      background: locationLoading || !exploreInput.trim() 
                        ? 'var(--text-muted)' 
                        : 'var(--primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: locationLoading || !exploreInput.trim() ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {locationLoading ? '...' : 'Go'}
                  </button>
                </div>
              </div>
            )}
            
            {currentProfile ? (
              <div className="fade-in" style={{ position: 'relative' }}>
                <div className="swipe-card"
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                  onMouseDown={onTouchStart}
                  onMouseMove={isSwipingRef.current ? onTouchMove : undefined}
                  onMouseUp={onTouchEnd}
                  onMouseLeave={() => { if (isSwipingRef.current) onTouchEnd(); }}
                  style={{
                  position: 'relative',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                  marginBottom: '20px',
                  transform: touchDelta ? `translateX(${touchDelta}px) rotate(${touchDelta * 0.08}deg)` : undefined,
                  transition: touchDelta ? 'none' : 'transform 0.3s ease',
                  cursor: user?.isIdentityVerified ? 'grab' : 'default',
                  userSelect: 'none',
                  touchAction: 'pan-y'
                }}>
                  {/* Swipe direction indicator overlays */}
                  {touchDelta > 50 && (
                    <div style={{
                      position: 'absolute', top: '50%', left: '20px', transform: 'translateY(-50%)',
                      zIndex: 30, background: 'rgba(81,207,102,0.9)', color: 'white',
                      padding: '8px 16px', borderRadius: '10px', fontSize: '18px', fontWeight: 700,
                      border: '3px solid white'
                    }}>LIKE 💚</div>
                  )}
                  {touchDelta < -50 && (
                    <div style={{
                      position: 'absolute', top: '50%', right: '20px', transform: 'translateY(-50%)',
                      zIndex: 30, background: 'rgba(255,107,107,0.9)', color: 'white',
                      padding: '8px 16px', borderRadius: '10px', fontSize: '18px', fontWeight: 700,
                      border: '3px solid white'
                    }}>NOPE ❌</div>
                  )}
                  {/* Photo — changes per slide, hidden on slide 3 */}
                  <img
                    src={(() => {
                      const photos = currentProfile.photos?.length > 0 ? currentProfile.photos : [{ url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800&q=80' }];
                      const idx = Math.min(discoverPhotoIndex, photos.length - 1);
                      return photos[idx]?.url;
                    })()}
                    alt={currentProfile.firstName}
                    style={{
                      width: '100%',
                      height: '450px',
                      objectFit: 'cover',
                      filter: !user?.isIdentityVerified ? 'blur(8px)' : 'none',
                      position: 'relative',
                      zIndex: 1,
                      pointerEvents: 'none',
                      opacity: discoverPhotoIndex === 3 ? 0 : 1,
                      transition: 'opacity 0.2s'
                    }}
                  />

                  {/* Tap zones — tap left/right to change slide (disabled on slide 3 for interactivity) */}
                  {user?.isIdentityVerified && discoverPhotoIndex !== 3 && (
                    <>
                      <div
                        onClick={() => { if (Math.abs(touchDelta) < 10) setDiscoverPhotoIndex(i => i > 0 ? i - 1 : 3); }}
                        style={{ position: 'absolute', top: 0, left: 0, width: '50%', height: '60%', cursor: 'pointer', zIndex: 15, background: 'transparent' }}
                      />
                      <div
                        onClick={() => { if (Math.abs(touchDelta) < 10) setDiscoverPhotoIndex(i => i < 3 ? i + 1 : 0); }}
                        style={{ position: 'absolute', top: 0, right: 0, width: '50%', height: '60%', cursor: 'pointer', zIndex: 15, background: 'transparent' }}
                      />
                    </>
                  )}

                  {/* Slide indicators */}
                  <div style={{
                    position: 'absolute', top: '12px', left: 0, right: 0,
                    display: 'flex', justifyContent: 'center', gap: '6px', zIndex: 16
                  }}>
                    {[0, 1, 2, 3].map(i => (
                      <div key={i} onClick={() => setDiscoverPhotoIndex(i)}
                        style={{
                          width: discoverPhotoIndex === i ? '24px' : '8px', height: '4px', borderRadius: '2px',
                          background: discoverPhotoIndex === i ? 'white' : 'rgba(255,255,255,0.5)',
                          transition: 'all 0.25s', cursor: 'pointer'
                        }} />
                    ))}
                  </div>
                  
                  {/* Locked Overlay for Unverified Users */}
                  {!user?.isIdentityVerified && (
                    <div style={{
                      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                      background: 'rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', padding: '20px', textAlign: 'center', zIndex: 10
                    }}>
                      <div style={{ fontSize: '64px', marginBottom: '16px' }}>🔒</div>
                      <h3 style={{ color: 'white', fontSize: '24px', fontWeight: 700, marginBottom: '12px' }}>Verify Your Identity</h3>
                      <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', marginBottom: '24px', maxWidth: '300px', lineHeight: '1.5' }}>
                        Complete identity verification to unlock full profiles and start matching!
                      </p>
                      <button onClick={() => onNeedsVerification()}
                        style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', padding: '16px 32px', borderRadius: '12px', fontSize: '16px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(102,126,234,0.4)' }}>
                        Verify Now
                      </button>
                      <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginTop: '16px' }}>You can see basic info, but full access requires verification</p>
                    </div>
                  )}
                  
                  {/* ====== SLIDE CONTENT OVERLAY ====== */}
                  <div style={{
                    position: 'absolute', 
                    ...(discoverPhotoIndex === 3 ? {
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      width: '100%',
                      height: '450px'
                    } : {
                      bottom: 0,
                      left: 0,
                      right: 0
                    }),
                    zIndex: 7,
                    background: discoverPhotoIndex === 3 
                      ? 'rgba(0,0,0,0.92)' 
                      : 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.5) 70%, transparent 100%)',
                    borderRadius: '0',
                    padding: discoverPhotoIndex === 3 ? '60px 20px 60px 20px' : '20px 16px 14px',
                    color: 'white',
                    pointerEvents: !user?.isIdentityVerified ? 'none' : 'auto',
                    opacity: !user?.isIdentityVerified ? 0.5 : 1,
                    display: discoverPhotoIndex === 3 ? 'flex' : 'block',
                    flexDirection: discoverPhotoIndex === 3 ? 'column' : undefined,
                    justifyContent: discoverPhotoIndex === 3 ? 'center' : undefined
                  }}>

                    {/* SLIDE 0: Name, Bio, Compatibility */}
                    {discoverPhotoIndex === 0 && (
                      <>
                        <h2 style={{ margin: '0 0 2px 0', fontSize: '24px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span>{currentProfile.firstName}, {currentProfile.age}</span>
                          {currentProfile.mbtiType && (
                            <span style={{ padding: '2px 8px', borderRadius: '9999px', background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px' }}>
                              {currentProfile.mbtiType}
                            </span>
                          )}
                        </h2>
                        <p style={{ margin: '0 0 6px 0', fontSize: '12px', opacity: 0.8 }}>
                          📍 {currentProfile.distance || 'Nearby'}
                          {currentProfile.occupation && <span> · {currentProfile.occupation}</span>}
                        </p>
                        <p style={{ margin: '0 0 8px 0', fontSize: '12px', lineHeight: '1.4', opacity: 0.95, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {currentProfile.bio || 'No bio yet'}
                        </p>

                        {/* Compact Compatibility */}
                        {(() => {
                          const c = currentProfile.compatibility;
                          const mbtiCompat = (profileForm.mbtiType && currentProfile.mbtiType)
                            ? getMbtiCompatibility(profileForm.mbtiType, currentProfile.mbtiType) : null;
                          const scores = [];
                          if (c?.interests > 0) scores.push({ label: 'Int', value: c.interests, emoji: '🎯' });
                          if (c?.survey > 0) scores.push({ label: 'Per', value: c.survey, emoji: '💭' });
                          if (c?.lifestyle > 0) scores.push({ label: 'Life', value: c.lifestyle, emoji: '🌟' });
                          if (mbtiCompat) scores.push({ label: 'MBTI', value: mbtiCompat.score, emoji: '🧩' });
                          const overallScore = c?.overall > 0 ? c.overall : scores.length > 0 ? Math.round(scores.reduce((sum, s) => sum + s.value, 0) / scores.length) : 0;
                          if (overallScore === 0 && scores.length === 0) return null;
                          const scoreColor = overallScore >= 75 ? '#22c55e' : overallScore >= 50 ? '#f59e0b' : '#ef4444';
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)', borderRadius: '12px', padding: '8px 12px' }}>
                              <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: scoreColor, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, flexShrink: 0 }}>
                                {overallScore}
                              </div>
                              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '12px', fontWeight: 600 }}>
                                  {overallScore >= 80 ? '🔥 Great Match' : overallScore >= 65 ? '✨ Good Match' : overallScore >= 45 ? '👋 Worth Exploring' : 'New Connection'}
                                </span>
                                {scores.map((s, i) => (
                                  <span key={i} style={{ fontSize: '10px', opacity: 0.7 }}>{s.emoji}{s.value}%</span>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                        <div style={{ textAlign: 'center', marginTop: '6px', fontSize: '11px', opacity: 0.4 }}>Tap photo to see more →</div>
                      </>
                    )}

                    {/* SLIDE 1: Interests (shared highlighted) */}
                    {discoverPhotoIndex === 1 && (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>
                            {currentProfile.firstName}'s Interests
                          </h3>
                          {(() => {
                            const shared = currentProfile.interests?.filter(i => profileForm.interests?.includes(i)) || [];
                            return shared.length > 0 ? <span style={{ fontSize: '11px', opacity: 0.6 }}>✓ {shared.length} shared</span> : null;
                          })()}
                        </div>
                        {currentProfile.interests && currentProfile.interests.length > 0 ? (
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', maxHeight: '130px', overflowY: 'auto' }}>
                            {currentProfile.interests.map((interest, i) => {
                              const isShared = profileForm.interests?.includes(interest);
                              return (
                                <span key={i} style={{
                                  background: isShared ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.12)',
                                  border: isShared ? '1.5px solid rgba(34,197,94,0.5)' : '1px solid rgba(255,255,255,0.1)',
                                  padding: '4px 11px', borderRadius: '9999px', fontSize: '12px',
                                  fontWeight: isShared ? 600 : 400
                                }}>
                                  {isShared && '✓ '}{interest}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <p style={{ fontSize: '14px', opacity: 0.7 }}>No interests listed yet</p>
                        )}
                      </>
                    )}

                    {/* SLIDE 2: About — profile details minus bio */}
                    {discoverPhotoIndex === 2 && (
                      <>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 700 }}>
                          About {currentProfile.firstName}
                        </h3>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '1fr 1fr', 
                          gap: '6px 12px',
                          alignItems: 'start'
                        }}>
                          {currentProfile.education && (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px', 
                              fontSize: '12px',
                              gridColumn: '1 / -1'  /* Full width */
                            }}>
                              <span style={{ opacity: 0.7, width: '20px', textAlign: 'center' }}>🎓</span>
                              <span>{currentProfile.education}</span>
                            </div>
                          )}
                          {currentProfile.height && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                              <span style={{ opacity: 0.7, width: '20px', textAlign: 'center' }}>📏</span>
                              <span>{currentProfile.height}cm</span>
                            </div>
                          )}
                          {currentProfile.location?.city && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                              <span style={{ opacity: 0.7, width: '20px', textAlign: 'center' }}>📍</span>
                              <span>{currentProfile.location.city}{currentProfile.location.country ? `, ${currentProfile.location.country}` : ''}</span>
                            </div>
                          )}
                          {currentProfile.lookingFor && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                              <span style={{ opacity: 0.7, width: '20px', textAlign: 'center' }}>💕</span>
                              <span>{currentProfile.lookingFor}</span>
                            </div>
                          )}
                          {currentProfile.religion && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                              <span style={{ opacity: 0.7, width: '20px', textAlign: 'center' }}>🙏</span>
                              <span>{currentProfile.religion}</span>
                            </div>
                          )}
                          {currentProfile.kids && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                              <span style={{ opacity: 0.7, width: '20px', textAlign: 'center' }}>👶</span>
                              <span>{{ have_want_more: 'Has kids, wants more', have_no_more: 'Has kids, done', no_want: "Wants kids", no_no_want: "No kids" }[currentProfile.kids] || currentProfile.kids}</span>
                            </div>
                          )}
                          {currentProfile.pets && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                              <span style={{ opacity: 0.7, width: '20px', textAlign: 'center' }}>🐾</span>
                              <span>{{ dog: '🐕 Dog owner', cat: '🐈 Cat owner', both: '🐕🐈 Dogs & cats', other: 'Has pets', want: 'Wants a pet', none: 'No pets' }[currentProfile.pets] || currentProfile.pets}</span>
                            </div>
                          )}
                          {(currentProfile.smoking || currentProfile.preferences?.lifestyle?.smoking) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                              <span style={{ opacity: 0.7, width: '20px', textAlign: 'center' }}>🚬</span>
                              <span>{currentProfile.preferences?.lifestyle?.smoking || currentProfile.smoking}</span>
                            </div>
                          )}
                          {(currentProfile.drinking || currentProfile.preferences?.lifestyle?.drinking) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                              <span style={{ opacity: 0.7, width: '20px', textAlign: 'center' }}>🍷</span>
                              <span>{currentProfile.preferences?.lifestyle?.drinking || currentProfile.drinking}</span>
                            </div>
                          )}
                          {(currentProfile.marijuana || currentProfile.preferences?.lifestyle?.cannabis) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                              <span style={{ opacity: 0.7, width: '20px', textAlign: 'center' }}>🌿</span>
                              <span>{currentProfile.preferences?.lifestyle?.cannabis || currentProfile.marijuana}</span>
                            </div>
                          )}
                          {!currentProfile.education && !currentProfile.height && !currentProfile.lookingFor && (
                            <p style={{ fontSize: '14px', opacity: 0.7 }}>No additional details shared yet</p>
                          )}
                        </div>
                      </>
                    )}

                    {/* SLIDE 3: Compatibility Breakdown */}
                    {discoverPhotoIndex === 3 && (
                      <>
                        {/* Navigation zone - Left side (tap to go back to slide 2) - INVISIBLE */}
                        <div 
                          onClick={() => setDiscoverPhotoIndex(2)}
                          style={{
                            position: 'absolute',
                            top: '15%',
                            left: 0,
                            width: '30%',
                            height: '70%',
                            cursor: 'pointer',
                            zIndex: 5,
                            background: 'transparent'
                          }}
                        />

                        {/* Navigation zone - Right side (tap to go forward to slide 0) - INVISIBLE */}
                        <div 
                          onClick={() => setDiscoverPhotoIndex(0)}
                          style={{
                            position: 'absolute',
                            top: '15%',
                            right: 0,
                            width: '30%',
                            height: '70%',
                            cursor: 'pointer',
                            zIndex: 5,
                            background: 'transparent'
                          }}
                        />

                        {/* Navigation zone - Top-Left corner (tap to go back to slide 2) */}
                        <div 
                          onClick={() => setDiscoverPhotoIndex(2)}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '50%',
                            height: '15%',
                            cursor: 'pointer',
                            zIndex: 5,
                            background: 'transparent'
                          }}
                        />

                        {/* Navigation zone - Top-Right corner (tap to go forward to slide 0) */}
                        <div 
                          onClick={() => setDiscoverPhotoIndex(0)}
                          style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: '50%',
                            height: '15%',
                            cursor: 'pointer',
                            zIndex: 5,
                            background: 'transparent'
                          }}
                        />

                        {/* Navigation zone - Bottom-Left corner (tap to go back to slide 2) */}
                        <div 
                          onClick={() => setDiscoverPhotoIndex(2)}
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '50%',
                            height: '15%',
                            cursor: 'pointer',
                            zIndex: 5,
                            background: 'transparent'
                          }}
                        />

                        {/* Navigation zone - Bottom-Right corner (tap to go forward to slide 0) */}
                        <div 
                          onClick={() => setDiscoverPhotoIndex(0)}
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: '50%',
                            height: '15%',
                            cursor: 'pointer',
                            zIndex: 5,
                            background: 'transparent'
                          }}
                        />

                        {user?.isIdentityVerified && currentProfile.compatibility && (() => {
                          const c = currentProfile.compatibility;
                          const mbtiCompat = (profileForm.mbtiType && currentProfile.mbtiType)
                            ? getMbtiCompatibility(profileForm.mbtiType, currentProfile.mbtiType) : null;
                          
                          const hasAnyScore = c.interests > 0 || c.survey > 0 || c.lifestyle > 0 || mbtiCompat;
                          if (!hasAnyScore) {
                            return (
                              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 700 }}>
                                  No Compatibility Data Yet
                                </h3>
                                <p style={{ fontSize: '14px', opacity: 0.7, lineHeight: 1.5 }}>
                                  Complete your profile to see how you match!
                                </p>
                              </div>
                            );
                          }
                      
                      const toggleScore = (type) => {
                        setExpandedScores(prev => ({ ...prev, [type]: !prev[type] }));
                      };
                      
                      // Get dynamic personalized insights
                      const getPersonalizedInterestsInsight = (score) => {
                        const shared = currentProfile.interests?.filter(i => profileForm.interests?.includes(i)) || [];
                        const sharedList = shared.length > 0 ? shared.slice(0, 3).join(', ') : '';
                        const name = currentProfile.firstName;
                        
                        const templates = {
                          high: [
                            `You and ${name} both enjoy ${sharedList || 'many of the same activities'}! These shared interests mean you'll have plenty to talk about on dates and can easily plan fun activities together.`,
                            `${name} loves ${sharedList || 'several things you do too'}. Common hobbies like these create natural conversation starters and give you built-in date ideas.`,
                            `Great news! ${shared.length > 0 ? `Both of you are into ${sharedList}` : 'You share many interests'}. This compatibility makes it easy to bond and spend time together doing what you both enjoy.`
                          ],
                          medium: [
                            `You share some interests with ${name}${sharedList ? ` including ${sharedList}` : ''}. While you don't love all the same things, this balance means you can introduce each other to new experiences.`,
                            `${name} and you have ${sharedList ? `${sharedList} in common, plus` : ''} some different hobbies. Couples often thrive with this mix - shared activities plus individual passions.`,
                            `Interesting! You overlap on some hobbies${sharedList ? ` like ${sharedList}` : ''}, with room for variety. This means quality time together plus space for personal growth.`
                          ],
                          low: [
                            `${name}'s interests are quite different from yours, which could actually be refreshing! You'll learn about new activities and expand your horizons together.`,
                            `You and ${name} have different hobbies, but that's not a dealbreaker. Many great couples support each other's unique passions while finding new things to try together.`,
                            `While your specific interests differ from ${name}'s, remember that opposites can complement beautifully. You'll bring fresh perspectives to each other's lives.`
                          ]
                        };
                        
                        const level = score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low';
                        const options = templates[level];
                        return options[Math.floor(Date.now() / 1000 / 3600) % options.length]; // Changes hourly
                      };
                      
                      const getPersonalizedSurveyInsight = (score) => {
                        const name = currentProfile.firstName;
                        const templates = {
                          high: [
                            `Your compatibility survey reveals strong alignment with ${name} in values, communication, and life approach. You likely handle conflicts similarly and share views on important topics.`,
                            `${name} and you think alike! Your survey answers show compatible views on social energy, planning styles, and what matters most in life.`,
                            `Great match! You and ${name} answered the personality questions in similar ways, suggesting you'll naturally understand each other's perspectives.`
                          ],
                          medium: [
                            `You align with ${name} on some core values, with complementary differences in others. Where you differ might balance each other out nicely.`,
                            `${name} shares some of your approaches to life, with enough variety to keep things interesting. This balance can create growth for both of you.`,
                            `Interesting dynamic! You and ${name} have some personality overlap plus distinct perspectives that could complement each other well.`
                          ],
                          low: [
                            `You and ${name} approach life quite differently based on your survey answers. With openness and communication, these differences can teach you both new ways of thinking.`,
                            `${name}'s personality style contrasts with yours - like an introvert and extrovert pairing. These differences often create balanced, dynamic relationships.`,
                            `Your survey answers show different perspectives from ${name}, which isn't necessarily bad. Successful couples often thrive on complementary personalities.`
                          ]
                        };
                        
                        const level = score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low';
                        const options = templates[level];
                        return options[(Math.floor(Date.now() / 1000 / 7200) + 1) % options.length]; // Changes every 2 hours, offset
                      };
                      
                      const getPersonalizedLifestyleInsight = (score) => {
                        const name = currentProfile.firstName;
                        const templates = {
                          high: [
                            `You and ${name} are aligned on lifestyle factors like kids, pets, and daily habits. This compatibility means fewer friction points and similar visions for the future.`,
                            `${name} shares your views on major lifestyle choices - kids, religion, pets, and habits. You're likely envisioning similar futures together.`,
                            `Strong lifestyle match with ${name}! Your compatible views on fundamental topics make it easier to build a shared life without major compromises.`
                          ],
                          medium: [
                            `You have some lifestyle compatibility with ${name}, with a few areas worth discussing. These aren't dealbreakers, just topics to explore openly early on.`,
                            `${name} and you align on some lifestyle factors with manageable differences in others. Open conversations can help you find middle ground.`,
                            `Partial lifestyle match with ${name}. Some shared views on big topics, some differences that may need communication and flexibility.`
                          ],
                          low: [
                            `Your lifestyles differ from ${name}'s in notable ways. Topics like kids or daily habits are important to discuss honestly before getting serious.`,
                            `${name}'s lifestyle preferences differ from yours significantly. While some couples navigate this, it requires mutual respect and clear boundaries.`,
                            `Different lifestyle visions from ${name} on important topics. These differences deserve open conversation about dealbreakers versus areas of compromise.`
                          ]
                        };
                        
                        const level = score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low';
                        const options = templates[level];
                        return options[(Math.floor(Date.now() / 1000 / 10800) + 2) % options.length]; // Changes every 3 hours, offset
                      };
                      
                      return (
                        <>
                          <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', position: 'relative', zIndex: 10 }}>
                            <span style={{ fontSize: '20px' }}>💫</span>
                            How You Match
                          </h3>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '320px', overflowY: 'auto', position: 'relative', zIndex: 10 }}>
                            {/* Interests */}
                            {c.interests > 0 && (
                              <div>
                                <div 
                                  onClick={() => toggleScore('interests')}
                                  style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '10px',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    background: 'rgba(255,255,255,0.05)',
                                    transition: 'background 0.2s'
                                  }}
                                >
                                  <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '10px',
                                    background: c.interests >= 70 ? 'rgba(34,197,94,0.25)' : c.interests >= 40 ? 'rgba(245,158,11,0.25)' : 'rgba(239,68,68,0.25)',
                                    border: `2px solid ${c.interests >= 70 ? 'rgba(34,197,94,0.6)' : c.interests >= 40 ? 'rgba(245,158,11,0.6)' : 'rgba(239,68,68,0.6)'}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    color: 'white',
                                    flexShrink: 0
                                  }}>
                                    {c.interests}%
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>
                                      🎯 Shared Interests
                                    </div>
                                    <div style={{ fontSize: '12px', opacity: 0.8, lineHeight: 1.3 }}>
                                      {c.interests >= 70 ? 'Many shared hobbies' : c.interests >= 40 ? 'Some interests in common' : 'Different hobbies'}
                                    </div>
                                  </div>
                                  <span style={{
                                    fontSize: '16px',
                                    transition: 'transform 0.2s',
                                    transform: expandedScores.interests ? 'rotate(180deg)' : 'rotate(0deg)',
                                    opacity: 0.7
                                  }}>▾</span>
                                </div>
                                {expandedScores.interests && (
                                  <div style={{
                                    marginTop: '6px',
                                    marginLeft: '16px',
                                    paddingLeft: '12px',
                                    borderLeft: '2px solid rgba(255,255,255,0.2)',
                                    fontSize: '12px',
                                    lineHeight: 1.5,
                                    opacity: 0.9
                                  }}>
                                    {getPersonalizedInterestsInsight(c.interests)}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Survey */}
                            {c.survey > 0 && (
                              <div>
                                <div 
                                  onClick={() => toggleScore('survey')}
                                  style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '10px',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    background: 'rgba(255,255,255,0.05)'
                                  }}
                                >
                                  <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '10px',
                                    background: c.survey >= 70 ? 'rgba(34,197,94,0.25)' : c.survey >= 40 ? 'rgba(245,158,11,0.25)' : 'rgba(239,68,68,0.25)',
                                    border: `2px solid ${c.survey >= 70 ? 'rgba(34,197,94,0.6)' : c.survey >= 40 ? 'rgba(245,158,11,0.6)' : 'rgba(239,68,68,0.6)'}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    color: 'white',
                                    flexShrink: 0
                                  }}>
                                    {c.survey}%
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>
                                      💭 Personality
                                    </div>
                                    <div style={{ fontSize: '12px', opacity: 0.8, lineHeight: 1.3 }}>
                                      {c.survey >= 70 ? 'Similar values & style' : c.survey >= 40 ? 'Some alignment' : 'Different approaches'}
                                    </div>
                                  </div>
                                  <span style={{
                                    fontSize: '16px',
                                    transition: 'transform 0.2s',
                                    transform: expandedScores.survey ? 'rotate(180deg)' : 'rotate(0deg)',
                                    opacity: 0.7
                                  }}>▾</span>
                                </div>
                                {expandedScores.survey && (
                                  <div style={{
                                    marginTop: '6px',
                                    marginLeft: '16px',
                                    paddingLeft: '12px',
                                    borderLeft: '2px solid rgba(255,255,255,0.2)',
                                    fontSize: '12px',
                                    lineHeight: 1.5,
                                    opacity: 0.9
                                  }}>
                                    {getPersonalizedSurveyInsight(c.survey)}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Lifestyle */}
                            {c.lifestyle > 0 && (
                              <div>
                                <div 
                                  onClick={() => toggleScore('lifestyle')}
                                  style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '10px',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    background: 'rgba(255,255,255,0.05)'
                                  }}
                                >
                                  <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '10px',
                                    background: c.lifestyle >= 70 ? 'rgba(34,197,94,0.25)' : c.lifestyle >= 40 ? 'rgba(245,158,11,0.25)' : 'rgba(239,68,68,0.25)',
                                    border: `2px solid ${c.lifestyle >= 70 ? 'rgba(34,197,94,0.6)' : c.lifestyle >= 40 ? 'rgba(245,158,11,0.6)' : 'rgba(239,68,68,0.6)'}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    color: 'white',
                                    flexShrink: 0
                                  }}>
                                    {c.lifestyle}%
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>
                                      🌟 Lifestyle
                                    </div>
                                    <div style={{ fontSize: '12px', opacity: 0.8, lineHeight: 1.3 }}>
                                      {c.lifestyle >= 70 ? 'Compatible habits' : c.lifestyle >= 40 ? 'Some differences' : 'Different lifestyles'}
                                    </div>
                                  </div>
                                  <span style={{
                                    fontSize: '16px',
                                    transition: 'transform 0.2s',
                                    transform: expandedScores.lifestyle ? 'rotate(180deg)' : 'rotate(0deg)',
                                    opacity: 0.7
                                  }}>▾</span>
                                </div>
                                {expandedScores.lifestyle && (
                                  <div style={{
                                    marginTop: '6px',
                                    marginLeft: '16px',
                                    paddingLeft: '12px',
                                    borderLeft: '2px solid rgba(255,255,255,0.2)',
                                    fontSize: '12px',
                                    lineHeight: 1.5,
                                    opacity: 0.9
                                  }}>
                                    {getPersonalizedLifestyleInsight(c.lifestyle)}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* MBTI */}
                            {mbtiCompat && (
                              <div>
                                <div 
                                  onClick={() => toggleScore('mbti')}
                                  style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '10px',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    background: 'rgba(255,255,255,0.05)'
                                  }}
                                >
                                  <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '10px',
                                    background: mbtiCompat.score >= 70 ? 'rgba(34,197,94,0.25)' : mbtiCompat.score >= 40 ? 'rgba(245,158,11,0.25)' : 'rgba(239,68,68,0.25)',
                                    border: `2px solid ${mbtiCompat.score >= 70 ? 'rgba(34,197,94,0.6)' : mbtiCompat.score >= 40 ? 'rgba(245,158,11,0.6)' : 'rgba(239,68,68,0.6)'}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    color: 'white',
                                    flexShrink: 0
                                  }}>
                                    {mbtiCompat.score}%
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>
                                      🧩 {profileForm.mbtiType} + {currentProfile.mbtiType}
                                    </div>
                                    <div style={{ fontSize: '12px', opacity: 0.8, lineHeight: 1.3 }}>
                                      {mbtiCompat.description}
                                    </div>
                                  </div>
                                  <span style={{
                                    fontSize: '16px',
                                    transition: 'transform 0.2s',
                                    transform: expandedScores.mbti ? 'rotate(180deg)' : 'rotate(0deg)',
                                    opacity: 0.7
                                  }}>▾</span>
                                </div>
                                {expandedScores.mbti && mbtiCompat.insight && (
                                  <div style={{
                                    marginTop: '6px',
                                    marginLeft: '16px',
                                    paddingLeft: '12px',
                                    borderLeft: '2px solid rgba(255,255,255,0.2)',
                                    fontSize: '12px',
                                    lineHeight: 1.5,
                                    opacity: 0.9
                                  }}>
                                    {mbtiCompat.insight}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div style={{
                            marginTop: '12px',
                            paddingTop: '10px',
                            borderTop: '1px solid rgba(255,255,255,0.15)',
                            fontSize: '11px',
                            opacity: 0.7,
                            textAlign: 'center',
                            position: 'relative',
                            zIndex: 10
                          }}>
                            💡 Tap any score for details
                          </div>
                        </>
                      );
                    })()}
                    
                    {/* Fallback for non-verified users */}
                    {!user?.isIdentityVerified && (
                      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 700 }}>
                          Verify to See Compatibility
                        </h3>
                        <p style={{ fontSize: '14px', opacity: 0.7, lineHeight: 1.5 }}>
                          Complete identity verification to see detailed compatibility insights
                        </p>
                      </div>
                    )}
                      </>
                    )}

                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '20px',
                  alignItems: 'center'
                }}>
                  <button
                    onClick={() => user?.isIdentityVerified && onSwipe('left')}
                    disabled={!user?.isIdentityVerified}
                    style={{
                      width: '70px',
                      height: '70px',
                      borderRadius: '50%',
                      border: '3px solid #ff6b6b',
                      background: 'white',
                      color: '#ff6b6b',
                      cursor: user?.isIdentityVerified ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s',
                      boxShadow: '0 4px 12px rgba(255,107,107,0.3)',
                      opacity: user?.isIdentityVerified ? 1 : 0.5
                    }}
                  >
                    <X size={32} strokeWidth={3} />
                  </button>

                  {/* Undo button - only shows if there's a last swipe */}
                  {lastSwipe && (
                    <button
                      onClick={onUndoLastSwipe}
                      title={`Undo ${lastSwipe.direction === 'right' ? 'like' : 'pass'} on ${lastSwipe.profile.firstName}`}
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        border: '2px solid #fbbf24',
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fbbf24 100%)',
                        color: '#92400e',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s',
                        boxShadow: '0 3px 10px rgba(251, 191, 36, 0.4)',
                        animation: 'pulse 2s infinite'
                      }}
                    >
                      <RotateCcw size={26} strokeWidth={2.5} />
                    </button>
                  )}

                  <button
                    onClick={() => user?.isIdentityVerified && onBlockUser(currentProfile.userId, 'discover')}
                    disabled={!user?.isIdentityVerified}
                    title="Block user"
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      border: '2px solid #6c757d',
                      background: 'white',
                      color: '#6c757d',
                      cursor: user?.isIdentityVerified ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s',
                      boxShadow: '0 2px 8px rgba(108,117,125,0.2)',
                      opacity: user?.isIdentityVerified ? 1 : 0.5
                    }}
                  >
                    <Ban size={24} strokeWidth={2} />
                  </button>

                  <button
                    onClick={() => user?.isIdentityVerified && onOpenReportModal(currentProfile.userId, currentProfile.firstName, 'discover')}
                    disabled={!user?.isIdentityVerified}
                    title="Report user"
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      border: '2px solid #e67e22',
                      background: 'white',
                      color: '#e67e22',
                      cursor: user?.isIdentityVerified ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s',
                      boxShadow: '0 2px 8px rgba(230,126,34,0.2)',
                      opacity: user?.isIdentityVerified ? 1 : 0.5
                    }}
                  >
                    <Flag size={22} strokeWidth={2} />
                  </button>

                  <button
                    onClick={() => user?.isIdentityVerified && onSwipe('right')}
                    disabled={!user?.isIdentityVerified}
                    style={{
                      width: '70px',
                      height: '70px',
                      borderRadius: '50%',
                      border: '3px solid #51cf66',
                      background: 'white',
                      color: '#51cf66',
                      cursor: user?.isIdentityVerified ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s',
                      boxShadow: '0 4px 12px rgba(81,207,102,0.3)',
                      opacity: user?.isIdentityVerified ? 1 : 0.5
                    }}
                  >
                    <Heart size={32} strokeWidth={3} />
                  </button>
                </div>

                {/* Compatibility is now in Slide 3 above */}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#95a5a6'
              }}>
                <Sparkles size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                <p>No more profiles right now. Check back later!</p>
              </div>
            )}
          </div>
  );
}
